import { FC, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputError,
  Label,
  Textarea,
} from '@features/ui';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (content: string) => Promise<void>;
}

const validationSchema = yup.object({
  content: yup.string().required('Note content is required'),
});

type FormData = {
  content: string;
};

export const AddNoteDialog: FC<AddNoteDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      content: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({ content: '' });
    }
  }, [open, reset]);

  const handleFormSubmit = async (data: FormData) => {
    await onSubmit(data.content);
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[500px] max-w-[90vw] mx-auto">
        <DialogHeader className="mb-5">
          <DialogTitle className="text-2xl text-center mb-[23px]">
            Add New Note
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-2">Note Content *</Label>
              <Controller
                control={control}
                name="content"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    hiddenHeader
                    className="min-h-[120px]"
                    placeholder="Enter note content..."
                  />
                )}
              />
              {errors.content && (
                <InputError>{errors.content.message}</InputError>
              )}
            </div>
          </div>

          <DialogFooter>
            <div className="flex items-center justify-center gap-10 mt-6">
              <Button
                className="w-20"
                disabled={isSubmitting}
                loading={isSubmitting}
                size="sm"
                type="submit"
                variant="lime"
              >
                Add
              </Button>
              <Button
                className="w-20"
                disabled={isSubmitting}
                size="sm"
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
